import { useState, useRef, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Form, Input, Checkbox, Typography, Divider } from 'antd'
import { UserOutlined, LockOutlined, IdcardOutlined } from '@ant-design/icons'
import { GoogleSignInButton } from './GoogleSignInButton'
import { LinkedInSignInButton } from './LinkedInSignInButton'
import { SurveyStepRole } from './SurveyStepRole'
import { SurveyStepUseCases } from './SurveyStepUseCases'
import { SurveyStepFrequency } from './SurveyStepFrequency'
import { FullWidthButton } from './styles/SignUpFormStyles'
import {
  FlowContainer,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  StepIndicator,
  StepContent,
  NavigationButtons,
  BackButton,
  ContinueButton,
} from './styles/SignUpFlowStyles'

const { Text } = Typography

// Step 1 = account info; Steps 2-4 = survey
const TOTAL_STEPS = 4
const AUTO_ADVANCE_DELAY_MS = 280

export interface SignUpFlowData {
  email: string
  password: string
  fullName: string
  termsAccepted: boolean
  newsletterOptIn: boolean
  // Step 2: Who is this workspace for? → survey_user_role
  surveyUserRole: string
  // Step 3: Where is your project at? → survey_ideation_frequency
  surveyProjectStage: string
  // Step 4: What would make today a win? → survey_use_cases (max 2)
  surveySessionGoals: string[]
}

interface SignUpFlowProps {
  onFinish: (values: SignUpFlowData) => void
  loading: boolean
}

export function SignUpFlow({ onFinish, loading }: SignUpFlowProps) {
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(1)

  const [accountInfo, setAccountInfo] = useState<{
    email: string
    password: string
    fullName: string
    termsAccepted: boolean
    newsletterOptIn: boolean
  } | null>(null)

  const [surveyUserRole, setSurveyUserRole] = useState<string | null>(null)
  const [surveyProjectStage, setSurveyProjectStage] = useState<string | null>(null)
  const [surveySessionGoals, setSurveySessionGoals] = useState<string[]>([])

  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
    }
  }, [])

  const scheduleAdvance = (toStep: number) => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current)
    autoAdvanceTimer.current = setTimeout(() => {
      setCurrentStep(toStep)
      autoAdvanceTimer.current = null
    }, AUTO_ADVANCE_DELAY_MS)
  }

  const handleAccountFormFinish = (values: {
    email: string
    password: string
    fullName: string
    termsAccepted: boolean
    newsletterOptIn: boolean
  }) => {
    setAccountInfo(values)
    setCurrentStep(2)
  }

  const handleRoleSelect = (value: string) => {
    setSurveyUserRole(value)
    scheduleAdvance(3)
  }

  const handleProjectStageSelect = (value: string) => {
    setSurveyProjectStage(value)
    scheduleAdvance(4)
  }

  const handleCreateAccount = () => {
    if (accountInfo && surveySessionGoals.length > 0) {
      onFinish({
        ...accountInfo,
        surveyUserRole: surveyUserRole ?? '',
        surveyProjectStage: surveyProjectStage ?? '',
        surveySessionGoals,
      })
    }
  }

  const progress = (currentStep / TOTAL_STEPS) * 100

  if (currentStep === 1) {
    return (
      <FlowContainer>
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill $progress={progress} />
          </ProgressBar>
          <StepIndicator>
            Step {currentStep} of {TOTAL_STEPS}
          </StepIndicator>
        </ProgressContainer>

        <Form
          form={form}
          name="signup"
          onFinish={handleAccountFormFinish}
          layout="vertical"
          requiredMark={false}
          size="large"
          initialValues={accountInfo ?? {}}
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <GoogleSignInButton label="Google" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LinkedInSignInButton label="LinkedIn" />
            </div>
          </div>

          <Divider plain>or</Divider>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Email" autoComplete="email" />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[
              { required: true, message: 'Please input your full name!' },
              { min: 2, message: 'Full name must be at least 2 characters!' },
            ]}
          >
            <Input prefix={<IdcardOutlined />} placeholder="Full Name" autoComplete="name" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  if (!/[A-Z]/.test(value))
                    return Promise.reject(
                      new Error('Password must contain at least one uppercase letter')
                    )
                  if (!/[a-z]/.test(value))
                    return Promise.reject(
                      new Error('Password must contain at least one lowercase letter')
                    )
                  if (!/[0-9]/.test(value))
                    return Promise.reject(new Error('Password must contain at least one number'))
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="termsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('You must agree to the terms and privacy policy')),
              },
            ]}
          >
            <Checkbox>
              <Text>
                I agree to the{' '}
                <RouterLink to="/terms" target="_blank">
                  Terms of Service
                </RouterLink>{' '}
                and{' '}
                <RouterLink to="/privacy" target="_blank">
                  Privacy Policy
                </RouterLink>
              </Text>
            </Checkbox>
          </Form.Item>

          <Form.Item name="newsletterOptIn" valuePropName="checked">
            <Checkbox>
              <Text>Subscribe to our newsletter for updates and news</Text>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <FullWidthButton type="primary" htmlType="submit">
              Continue
            </FullWidthButton>
          </Form.Item>
        </Form>
      </FlowContainer>
    )
  }

  return (
    <FlowContainer>
      <ProgressContainer>
        <ProgressBar>
          <ProgressFill $progress={progress} />
        </ProgressBar>
        <StepIndicator>
          Step {currentStep} of {TOTAL_STEPS}
        </StepIndicator>
      </ProgressContainer>

      <StepContent key={currentStep}>
        {currentStep === 2 && <SurveyStepRole value={surveyUserRole} onChange={handleRoleSelect} />}

        {currentStep === 3 && (
          <SurveyStepFrequency value={surveyProjectStage} onChange={handleProjectStageSelect} />
        )}

        {currentStep === 4 && (
          <SurveyStepUseCases values={surveySessionGoals} onChange={setSurveySessionGoals} />
        )}
      </StepContent>

      {/* Step 2: card click advances; just a back button */}
      {currentStep === 2 && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <BackButton onClick={() => setCurrentStep(1)}>← Back</BackButton>
        </div>
      )}

      {/* Step 3: card click advances; just a back button */}
      {currentStep === 3 && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <BackButton onClick={() => setCurrentStep(2)}>← Back</BackButton>
        </div>
      )}

      {/* Step 4: full row with Back + Create Account */}
      {currentStep === 4 && (
        <NavigationButtons>
          <BackButton onClick={() => setCurrentStep(3)}>Back</BackButton>
          <ContinueButton
            onClick={handleCreateAccount}
            disabled={surveySessionGoals.length === 0 || loading}
            $disabled={surveySessionGoals.length === 0 || loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </ContinueButton>
        </NavigationButtons>
      )}
    </FlowContainer>
  )
}
